// Định dạng ngày / giờ / số / tiền THEO NGÔN NGỮ ĐANG HIỂN THỊ.
//
// ══ VÌ SAO CÓ FILE NÀY ══
// Chữ đã dịch xong nhưng 588 chỗ trong repo còn hard-code `toLocaleDateString('vi-VN')`
// ⇒ màn hình tiếng Anh vẫn hiện "Chủ Nhật, 09/08/2026" và "1.234.567 đ". Người Canada đọc
// "09/08" là ngày 8 tháng 9 (họ quen mm/dd) — sai NGÀY chứ không chỉ sai chữ.
//
// ══ 3 ĐIỀU KHÔNG ĐƯỢC PHÁ ══
// 1. **Múi giờ là MÚI HIỂN THỊ CỦA HỆ, không theo ngôn ngữ** (displayTimezone.js): DB lưu UTC, FE hiển
//    thị theo múi của WORKSPACE vì nghiệp vụ (chấm công, hạn việc, ngày ký) tính theo đồng hồ công ty bất
//    kể người xem ngồi đâu. Work + Core không khai ⇒ Asia/Ho_Chi_Minh như trước (memory
//    feedback_datetime_utc_strict); Site Connect khai `company_timezone` của workspace (12/10/2026 — khách
//    Toronto thấy tin nhắn 13:00Z in «08:00 p.m.»). Đổi ngôn ngữ chỉ đổi CÁCH VIẾT, KHÔNG đổi múi giờ.
//    Múi riêng TỪNG NGƯỜI là chuyện khác (`LOCAL_TZ`, zoneClock.js) — đừng gộp vào đây.
//    ⚠️ NGÀY LỊCH không qua múi giờ: chuỗi 'YYYY-MM-DD' (cột DATE), Date đúng nửa đêm UTC hay nửa đêm MÁY
//    (lịch dựng bằng `new Date(y, m, d)`) in ĐÚNG ngày đó — xem `ngayLich()`. Giờ VN (+7) che lỗi này vì
//    00:00Z luôn còn cùng ngày ở VN; múi âm (Toronto −4) thì `new Date('2026-10-12')` là 20:00 ngày 11.
// 2. **'sv-SE' là format MÁY, KHÔNG phải ngôn ngữ.** 7 chỗ trong repo dùng nó để lấy
//    "YYYY-MM-DD" (so sánh với DATE column của BE, value của <input type="date">).
//    ⛔ TUYỆT ĐỐI không thay 'sv-SE' bằng locale động — sẽ gửi "09/08/2026" xuống BE.
//    Dùng `todayVN()`/`toYMD()` của utils/dateVN.js cho việc đó.
// 3. **Tiền tệ vẫn là VND.** Bán ở Canada thì giá vẫn niêm yết VND cho tới khi có bảng giá
//    CAD thật; đổi ký hiệu sang "$" mà số tiền vẫn là đồng là lừa người đọc. Nên: số
//    tách nhóm theo locale (1,234,567 cho en-CA · 1 234 567 cho fr-CA), ký hiệu giữ ₫.
//
// 4. **Ngày viết BẰNG SỐ theo chuẩn của HỆ, không theo ngôn ngữ** (03/10/2026, xem dateOrder.js):
//    Site Connect mm/dd/yyyy · Work + Core dd/mm/yyyy — ở MỌI ngôn ngữ. Ngày viết bằng CHỮ
//    (`medium`, `long`: «Aug 9, 2026», «9 tháng 8») vẫn để Intl lo theo ngôn ngữ.
//
// Dùng ở đâu: import từ '@cnv/ui/i18n' (hoặc '../../i18n/format.js' trong cnv-ui).
//   fmtDate(d)                    → 09/08/2026 (Work, Core) · 08/09/2026 (Site Connect) — mọi ngôn ngữ
//   fmtYmd('2026-08-09')          → như trên, cho chuỗi ngày thuần của máy chủ (không qua múi giờ)
//   fmtDate(d, 'medium')          → 9 thg 8, 2026 · Aug 9, 2026 · 9 août 2026
//   fmtDate(d, 'long')            → Chủ Nhật, 9 tháng 8, 2026 · Sunday, August 9, 2026
//   fmtDateTime(d)                → 09/08/2026 14:30 · 09/08/2026 2:30 p.m. (giờ theo ngôn ngữ)
//   fmtNumber(1234567)            → 1.234.567 · 1,234,567 · 1 234 567
//   fmtMoney(1234567)             → 1.234.567 ₫ (nhóm số theo locale, ký hiệu giữ ₫)
//   fmtCurrency(13072, 'CAD')     → 13.072 CA$ · $13,072 · 13 072 $ (tiền tệ máy chủ trả)
//   fmtPercent(0.4567)            → 45,7% · 45.7%

import { getLocale } from './runtime.js';
import { getLocaleMeta } from './locales.js';
import { joinDateParts } from './dateOrder.js';
import { getDisplayTimezone, DEFAULT_DISPLAY_TIMEZONE } from './displayTimezone.js';

/** Mã BCP-47 đầy đủ của ngôn ngữ đang hiển thị ('vi-VN' | 'en-CA' | 'fr-CA' | 'es-419'). */
export function intlLocale() {
  return getLocaleMeta(getLocale())?.intl || 'vi-VN';
}

// Cache Intl formatter — dựng mới mỗi lần render là chậm thật (Intl constructor đắt),
// bảng này nhỏ (số locale × số preset) nên giữ nguyên vòng đời trang.
const cache = new Map();
function fmtOf(kind, locale, opts) {
  const key = `${kind}|${locale}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = kind === 'num' ? new Intl.NumberFormat(locale, opts) : new Intl.DateTimeFormat(locale, opts);
    cache.set(key, f);
  }
  return f;
}

// ── Ngày / giờ ───────────────────────────────────────────────────────────────
//
// Preset thay vì bắt call site tự truyền options: 588 chỗ đang dùng ~6 kiểu khác nhau,
// gom về preset thì codemod thay được máy móc và bản dịch nhất quán toàn app.
const DATE_PRESETS = {
  short:  { day: '2-digit', month: '2-digit', year: 'numeric' },   // 09/08/2026 — thứ tự theo HỆ (ngaySo)
  dayMonth: { day: '2-digit', month: '2-digit' },                  // 09/08 — thứ tự theo HỆ (ngaySo)
  medium: { day: 'numeric', month: 'short', year: 'numeric' },     // 9 thg 8, 2026
  long:   { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  monthYear: { month: 'long', year: 'numeric' },                   // tháng 8, 2026
};

function toDate(d) {
  if (d == null || d === '') return null;
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

// ── NGÀY LỊCH ≠ MỐC THỜI GIAN ─────────────────────────────────────────────────────────────────────
// Một ngày lịch (hạn việc, ngày nghỉ, ô của lưới lịch) không thuộc múi giờ nào — đem nó qua múi hiển thị là
// lệch ngày. Ba dáng gặp thật trong kernel:
//   1. chuỗi 'YYYY-MM-DD' của cột DATE — `new Date('2026-10-12')` = 00:00Z, ở Toronto là 20:00 NGÀY 11;
//   2. Date đúng 00:00:00.000 UTC — chỗ gọi tự `new Date(ymd)` rồi mới truyền vào;
//   3. Date đúng 00:00:00.000 theo MÁY — lưới lịch dựng bằng `new Date(y, m, d)` / `setHours(0,0,0,0)`.
// Dáng 1 luôn là ngày lịch. Dáng 2–3 chỉ coi là ngày lịch khi chỗ gọi xin CHỈ NGÀY (không giờ) và không tự
// truyền `timeZone`. Giờ VN (Work, Core) cho ra Y NGUYÊN như trước ở cả ba dáng; một mốc thật rơi trúng nửa
// đêm tới từng mili-giây thì in ngày lịch đó — chấp nhận được.
// Trả { y, m, d } hoặc null (= cứ đọc như mốc thời gian theo múi hiển thị).
const NGAY_THUAN = /^(\d{4})-(\d{2})-(\d{2})$/;
export function ngayLich(raw, dt, { chiNgay = true } = {}) {
  if (typeof raw === 'string') {
    const m = NGAY_THUAN.exec(raw.trim());
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  }
  if (!chiNgay || !dt) return null;
  if (dt.getUTCHours() === 0 && dt.getUTCMinutes() === 0 && dt.getUTCSeconds() === 0 && dt.getUTCMilliseconds() === 0) {
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }
  if (dt.getHours() === 0 && dt.getMinutes() === 0 && dt.getSeconds() === 0 && dt.getMilliseconds() === 0) {
    return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
  }
  return null;
}
const coGio = (o) => !!(o.hour || o.minute || o.second || o.timeStyle || o.dayPeriod || o.fractionalSecondDigits);

/**
 * Chọn (mốc, múi) để đưa vào Intl khi in NGÀY: ngày lịch ⇒ giữa trưa UTC của ngày đó + múi 'UTC' (không trôi);
 * còn lại ⇒ chính mốc + múi chỗ gọi truyền, không có thì múi hiển thị. Dùng chung với utils/dateVN.js.
 */
export function mocVaMuiChoNgay(raw, dt, opts = {}) {
  const lich = ngayLich(raw, dt, { chiNgay: !opts.timeZone && !coGio(opts) });
  if (lich) return { dt: new Date(Date.UTC(lich.y, lich.m - 1, lich.d, 12)), timeZone: 'UTC' };
  return { dt, timeZone: opts.timeZone || getDisplayTimezone() };
}

// ── Ngày VIẾT BẰNG SỐ: thứ tự theo chuẩn của HỆ, KHÔNG theo ngôn ngữ (xem dateOrder.js) ──────────
// Intl tự sắp ngày số theo CLDR của từng ngôn ngữ: en-CA ra `2026-09-28`, vi-VN `dayMonth` ra `28-09`
// (gạch ngang) — cùng một ngày ba kiểu tuỳ người xem chọn tiếng gì. Mọi tuỳ chọn chỉ gồm ngày + tháng
// (+ năm) bằng số đi đường riêng: Intl chỉ lo tách CON SỐ theo đúng múi giờ, thứ tự + dấu «/» là của hệ.
const SO = new Set(['numeric', '2-digit']);
function laNgaySo(o) {
  return !!o && SO.has(o.day) && SO.has(o.month) && (!o.year || SO.has(o.year))
    && !o.weekday && !o.era && !o.hour && !o.minute && !o.second && !o.dateStyle && !o.timeStyle;
}
function ngaySo(dt, o) {
  // `numberingSystem: 'latn'` — máy đặt tiếng Ả Rập/Thái thì Intl trả chữ số bản địa (xem utils/zoneClock.js).
  const p = {};
  for (const x of fmtOf('date', intlLocale(), {
    timeZone: o.timeZone || getDisplayTimezone(), year: 'numeric', month: 'numeric', day: 'numeric', numberingSystem: 'latn',
  }).formatToParts(dt)) p[x.type] = x.value;
  const pad = (v, kieu) => (kieu === '2-digit' ? String(v).padStart(2, '0') : String(Number(v)));
  const nam = !o.year ? '' : (o.year === '2-digit' ? String(p.year).slice(-2) : String(p.year));
  return joinDateParts(pad(p.day, o.day), pad(p.month, o.month), nam);
}

/**
 * @param {Date|string|number} d
 * @param {'short'|'dayMonth'|'medium'|'long'|'monthYear'|object} [preset='short']
 *   Truyền object = options Intl thô (khi preset không đủ).
 * @param {string} [fallback=''] trả về khi d rỗng/không parse được
 */
export function fmtDate(d, preset = 'short', fallback = '') {
  const dt = toDate(d);
  if (!dt) return fallback;
  const opts = typeof preset === 'string' ? (DATE_PRESETS[preset] || DATE_PRESETS.short) : preset;
  const c = mocVaMuiChoNgay(d, dt, opts);
  const o = { ...opts, timeZone: c.timeZone };
  if (laNgaySo(o)) return ngaySo(c.dt, o);
  return fmtOf('date', intlLocale(), o).format(c.dt);
}

/**
 * Ngày THUẦN 'YYYY-MM-DD' (cột DATE của máy chủ: hạn, ngày bắt đầu, ngày ca) → chữ theo chuẩn của hệ.
 * Không qua `Date`/múi giờ nào cả — chuỗi ngày là ngày, đổi múi là lệch một hôm.
 * `year: false` ⇒ chỉ ngày + tháng (cột hẹp: việc con, báo cáo theo ngày).
 */
export function fmtYmd(ymd, { year = true } = {}, fallback = '') {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return fallback;
  return joinDateParts(m[3], m[2], year ? m[1] : '');
}

/**
 * Ngày + giờ:phút. `withSeconds` khi cần giây (log, audit).
 *
 * ⚠️ GHÉP TAY `ngày + ' ' + giờ` chứ KHÔNG đưa cả hai vào một Intl.DateTimeFormat.
 * Đo thật: `new Intl.DateTimeFormat('vi-VN', {...date, hour, minute})` trả
 * **"14:30 09/08/2026"** — ICU đặt GIỜ TRƯỚC NGÀY cho vi-VN. Toàn bộ repo (và người
 * dùng VN) đang quen "09/08/2026 14:30"; để Intl tự sắp là tự dưng đảo thứ tự trên
 * mọi màn hình tiếng Việt — đổi ngôn ngữ hiển thị KHÔNG được làm xáo trộn tiếng Việt.
 * Ghép tay giữ thứ tự ngày→giờ nhất quán ở cả 4 ngôn ngữ, phần TỪNG VẾ vẫn do Intl lo
 * (en-CA "2:30 p.m." · fr-CA "14 h 30" · es-419 "02:30 p.m.").
 */
export function fmtDateTime(d, { preset = 'short', withSeconds = false, tz = null } = {}, fallback = '') {
  const dt = toDate(d);
  if (!dt) return fallback;
  // `tz`: múi giờ đọc mốc (vd LOCAL_TZ = lịch máy người xem). Không truyền ⇒ múi hiển thị (điều 1 đầu tệp).
  // Vế ngày ĐI CÙNG MÚI với vế giờ (truyền `timeZone` tường minh) ⇒ mốc nửa đêm không bị đọc thành ngày lịch
  // rồi ghép với giờ của ngày khác. Riêng chuỗi 'YYYY-MM-DD' vẫn in đúng ngày đó (ngayLich dáng 1).
  const p = typeof preset === 'string' ? (DATE_PRESETS[preset] || DATE_PRESETS.short) : preset;
  const ngay = fmtDate(typeof d === 'string' ? d : dt, { ...p, timeZone: tz || p.timeZone || getDisplayTimezone() });
  return `${ngay} ${fmtTime(dt, { withSeconds, tz })}`;
}

/** Chỉ giờ:phút — theo múi hiển thị (Work/Core: giờ VN), hoặc theo `tz` khi truyền (vd LOCAL_TZ). */
export function fmtTime(d, { withSeconds = false, tz = null } = {}, fallback = '') {
  const dt = toDate(d);
  if (!dt) return fallback;
  return fmtOf('date', intlLocale(), {
    timeZone: tz || getDisplayTimezone(), hour: '2-digit', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  }).format(dt);
}

/**
 * Múi giờ của MÁY người xem. Những mốc trước nay in bằng `toLocaleString()` trần (nhật ký ca, lượt
 * check-in, link chia sẻ…) đọc theo lịch máy — đổi sang hàm của tệp này thì truyền `LOCAL_TZ` để KHÔNG
 * đổi ngày của người ngồi Toronto (mốc 23:00 bên đó đã là sáng hôm sau ở VN).
 */
export const LOCAL_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_DISPLAY_TIMEZONE; } catch { return DEFAULT_DISPLAY_TIMEZONE; }
})();

/**
 * Tên THỨ theo ngôn ngữ hiển thị — dùng cho tiêu đề cột lịch/bảng công.
 *
 * ⚠️ `'compact'` KHÔNG map thẳng sang `weekday:'short'` của Intl, vì CLDR lệch nhau:
 *   • vi-VN  short = "Thứ 2" (5 ký tự) → vỡ ô lịch tháng rộng 28px, và người Việt đọc
 *     lịch quen "T2". Dạng "T2…CN" chính là `narrow` của CLDR vi.
 *   • en/fr/es narrow = 1 chữ cái (M T W T F S S — hai chữ T, hai chữ S) → không phân
 *     biệt được cột, phải dùng `short` ("Mon", "lun.", "lun").
 * Nên: vi → narrow, còn lại → short. Đừng "dọn" thành một preset duy nhất.
 *
 * Nhận cả chuỗi 'YYYY-MM-DD' và Date dựng ở nửa đêm (lưới lịch) — in đúng THỨ của ngày lịch đó ở mọi múi
 * hiển thị (`ngayLich`). Trước 12/10/2026 việc này chỉ đúng nhờ giờ VN (+7) không bao giờ đẩy 00:00Z sang ngày
 * khác; Site Connect hiển thị theo Toronto (−4) thì Chủ nhật 00:00Z thành thứ Bảy.
 */
export function fmtWeekday(d, preset = 'compact', fallback = '') {
  const dt = toDate(d);
  if (!dt) return fallback;
  const loc = intlLocale();
  const weekday = preset === 'long' ? 'long' : (loc.startsWith('vi') ? 'narrow' : 'short');
  const c = mocVaMuiChoNgay(d, dt, { weekday });
  return fmtOf('date', loc, { timeZone: c.timeZone, weekday }).format(c.dt);
}

/**
 * Bảy nhãn thứ theo ngôn ngữ đang hiển thị, dùng cho tiêu đề cột lịch: vi → "CN T2 … T7",
 * en → "Sun Mon … Sat", fr → "dim. lun. … sam.". `start = 1` ⇒ bắt đầu từ thứ Hai.
 * Thay cho mảng cứng `['CN','T2',…]` — mảng đó không có dấu nên không cổng i18n nào bắt được,
 * và bản EN của Site Connect in "T2 T3…" trên lịch (đo 10/10/2026).
 */
export function weekdayLabels({ start = 0, preset = 'compact' } = {}) {
  // 07/01/2024 là Chủ nhật. Chuỗi NGÀY LỊCH (ngayLich) ⇒ đúng thứ ở mọi múi hiển thị — bản cũ lấy giữa trưa UTC,
  // đủ cho giờ VN nhưng sang múi +13 (Auckland mùa hè) là trưa UTC đã là 01:00 hôm sau.
  return Array.from({ length: 7 }, (_, i) => fmtWeekday(`2024-01-${String(7 + ((i + start) % 7)).padStart(2, '0')}`, preset));
}

/**
 * Khoảng thời gian tương đối ("3 ngày trước", "3 days ago", "il y a 3 jours").
 * Dùng Intl.RelativeTimeFormat — KHÔNG tự ghép chuỗi + số, vì tiếng Pháp/Tây Ban Nha
 * đổi cả giới từ theo hướng thời gian.
 */
export function fmtRelative(d, fallback = '') {
  const dt = toDate(d);
  if (!dt) return fallback;
  const diffMs = dt.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const UNITS = [
    ['year', 31536e6], ['month', 2592e6], ['day', 864e5],
    ['hour', 36e5], ['minute', 6e4], ['second', 1e3],
  ];
  const [unit, ms] = UNITS.find(([, u]) => abs >= u) || ['second', 1e3];
  const key = `rel|${intlLocale()}`;
  let f = cache.get(key);
  if (!f) { f = new Intl.RelativeTimeFormat(intlLocale(), { numeric: 'auto' }); cache.set(key, f); }
  return f.format(Math.round(diffMs / ms), unit);
}

// ── Số / tiền / phần trăm ────────────────────────────────────────────────────

/** Số nguyên/thập phân tách nhóm theo locale. `d` = số chữ số thập phân. */
export function fmtNumber(n, { d = 0, maxD } = {}, fallback = '') {
  if (n == null || n === '' || Number.isNaN(Number(n))) return fallback;
  return fmtOf('num', intlLocale(), {
    minimumFractionDigits: d,
    maximumFractionDigits: maxD ?? d,
  }).format(Number(n));
}

/**
 * Ký hiệu tiền của `fmtMoney` — xuất ra để ô nhập tiền (hậu tố «₫» bên phải ô) dùng ĐÚNG ký
 * hiệu mà dòng hiển thị cùng trang in ra. Trước 21/09/2026 các ô nhập ghi cứng chữ «đ» (chữ cái
 * tiếng Việt, không phải ký hiệu tiền) nên cùng một trang vừa «60.000 ₫» vừa «đ» ở ô nhập.
 */
export const KY_HIEU_TIEN = '₫';

/**
 * Tiền VND. Nhóm số theo locale, ký hiệu ₫ giữ nguyên (xem điều 3 ở đầu file).
 * `symbol=false` khi chỗ hiển thị đã có nhãn đơn vị riêng.
 */
export function fmtMoney(n, { symbol = true, d = 0 } = {}, fallback = '') {
  if (n == null || n === '' || Number.isNaN(Number(n))) return fallback;
  const num = fmtNumber(n, { d });
  return symbol ? `${num} ${KY_HIEU_TIEN}` : num;
}

/**
 * Tiền theo MÃ TIỀN TỆ MÁY CHỦ TRẢ (vd `tenant_settings.default_currency` = 'CAD' của Sky, hay
 * `currency` của đơn giá công) — KHÁC `fmtMoney` (VND, ký hiệu ₫ cố định, điều 3 ở đầu file:
 * con số là đồng thì không được khoác «$»). Ở đây con số ĐÚNG LÀ tiền tệ đó, nên để Intl đặt ký
 * hiệu + nhóm số theo ngôn ngữ đang hiển thị: CAD → vi «13.072 CA$» · en-CA «$13,072» · fr-CA
 * «13 072 $». Không có mã ⇒ số trần như `fmtNumber`; mã lạ Intl không biết ⇒ «số MÃ».
 *
 * Vì sao cần (22/09/2026, sếp Phú: "mấy cái liên quan tiền thì cần format number"): lưới Xếp việc
 * và hồ sơ dự án tự gọi `toLocaleString(undefined)` / `Intl.NumberFormat(undefined)` — theo
 * ngôn ngữ của TRÌNH DUYỆT, không theo ngôn ngữ đang chọn trong app ⇒ màn tiếng Việt vẫn in
 * «13,072 CAD» kiểu Mỹ.
 */
export function fmtCurrency(n, ma, { d = 0 } = {}, fallback = '') {
  if (n == null || n === '' || Number.isNaN(Number(n))) return fallback;
  if (!ma) return fmtNumber(n, { d });
  const maTien = String(ma).trim().toUpperCase();
  try {
    return fmtOf('num', intlLocale(), {
      style: 'currency', currency: maTien, minimumFractionDigits: d, maximumFractionDigits: d,
    }).format(Number(n));
  } catch {
    return `${fmtNumber(n, { d })} ${maTien}`;
  }
}

/**
 * Phần trăm. Nhận SỐ ĐÃ NHÂN 100 mặc định (0-100) vì repo đang lưu vậy;
 * `ratio: true` cho giá trị 0-1.
 */
export function fmtPercent(n, { d = 1, ratio = false } = {}, fallback = '') {
  if (n == null || n === '' || Number.isNaN(Number(n))) return fallback;
  const v = ratio ? Number(n) : Number(n) / 100;
  return fmtOf('num', intlLocale(), {
    style: 'percent', minimumFractionDigits: d, maximumFractionDigits: d,
  }).format(v);
}

/** Dung lượng file (KB/MB/GB) — số theo locale, đơn vị là ký hiệu quốc tế. */
export function fmtBytes(bytes, fallback = '') {
  // `bytes === ''` phải rơi vào fallback: Number('') = 0 (KHÔNG phải NaN) nên chuỗi rỗng
  // từ API sẽ hiện "0 B" — người đọc tưởng file rỗng thật thay vì thiếu dữ liệu.
  if (bytes == null || bytes === '' || Number.isNaN(Number(bytes))) return fallback;
  const b = Number(bytes);
  if (b < 1024) return `${fmtNumber(b)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${fmtNumber(v, { d: v < 10 ? 1 : 0 })} ${units[i]}`;
}
